















































import { Component, Prop, Vue } from 'vue-property-decorator';
import { getCampaignMergeOptionsUseCase } from '@/core/campaign/usecases/getCampaignMergeOptionsUseCase';
import { mergeCampaignIntoUseCase } from '@/core/campaign/usecases/mergeCampaignIntoUseCase';
import type { Campaign } from '@/core/campaign/models/Campaign';
import getLogger from '@/shared/logger/logger';
import ConfirmCampaignMergeDialog from '@/ui/components/organisms/dialogs/ConfirmCampaignMergeDialog.vue';
import ButtonWithText from '@/ui/components/atoms/buttons/ButtonWithText.vue';
import { AlertStoreActions, AlertType } from '@/ui/stores/AlertStore';
import { isNotEmpty } from '@/ui/services/admin/InputVerification';
import DialogTitle from '@/ui/components/atoms/textItems/DialogTitle.vue';
import { CampaignStoreGetters, CampaignStoreMutations } from '@/ui/stores/CampaignStore';
import { ROUTE_NAMES } from '@/ui/constant/routes.constants';

type SelectItemDescription = {
  text: string;
  // campaign id
  value: string;
};
interface VForm extends HTMLFormElement {
  validate(): boolean;
  reset(): boolean;
}

const LOG = getLogger('SelectDestinationCampaignMergeDialog');

@Component({
  name: 'SelectDestinationCampaignMergeDialog',
  components: { ButtonWithText, ConfirmCampaignMergeDialog, DialogTitle },
})
export default class SelectDestinationCampaignMergeDialog extends Vue {
  @Prop({ required: true }) showDialog!: boolean;
  @Prop({ required: true }) sourceCampaignId!: string;

  isLoading = false;

  isFormValid = false;
  possibleDestinations: Campaign[] = [];
  possibleDestinationSelectOptions: SelectItemDescription[] = [];
  selectedDestinationId: string = '';

  isConfirmOpen = false;

  get source(): Campaign | undefined {
    return this.$store.getters[CampaignStoreGetters.GET_SELECTED_CAMPAIGN];
  }

  get selectedDestination(): Campaign | null {
    return this.selectedDestinationId
      ? this.possibleDestinations.find((campaign) => campaign.id === this.selectedDestinationId) ?? null
      : null;
  }
  get currentUser() {
    return this.$store.state.userStore.currentUser;
  }
  get isNotEmpty() {
    return isNotEmpty;
  }

  async mounted() {
    await getCampaignMergeOptionsUseCase({ sourceId: this.sourceCampaignId }, this.currentUser)
      .then((campaigns) => {
        if (!campaigns) {
          LOG.warn('undefined campaign data, this might be OK if the request was cancelled');
          return;
        }
        this.possibleDestinations = campaigns;
        this.possibleDestinationSelectOptions = this.possibleDestinations.map((campaign) => ({
          text: `${campaign.title} - (${campaign.id})`,
          value: campaign.id,
        }));
      })
      .catch((error) => {
        LOG.error('error getting campaign merge options', error);
        this.$store.dispatch(AlertStoreActions.TRIGGER_ALERT, {
          message: 'Failed to retrieve options for merge',
          type: AlertType.ERROR,
        });
      });
  }

  closeDialog() {
    LOG.debug('emitting close for select merge destination dialog');
    this.$emit('close');
  }

  askForMergeConfirmation() {
    if (this.isAllInputValid()) {
      this.isConfirmOpen = true;
    }
  }
  closeConfirmDialog() {
    this.isConfirmOpen = false;
  }
  doMerge() {
    this.isConfirmOpen = false;
    this.isLoading = true;
    if (!this.selectedDestinationId) {
      throw new Error('no selectedDestinationId for merge');
    }
    mergeCampaignIntoUseCase(
      { sourceId: this.sourceCampaignId, destinationId: this.selectedDestinationId },
      this.currentUser,
    )
      .then(() => {
        this.$store.dispatch(AlertStoreActions.TRIGGER_ALERT, {
          message: 'campaign.merge-campaign.success',
          type: AlertType.SUCCESS,
        });

        setTimeout(
          () =>
            this.$store.dispatch(CampaignStoreMutations.SET_CAMPAIGNS).then(() => {
              this.$router.push({
                name: ROUTE_NAMES.CAMPAIGNS,
              });
            }),
          1_000,
        );
      })
      .catch((error) => {
        LOG.error('failed to merge campaigns', error);
        this.$store.dispatch(AlertStoreActions.TRIGGER_ALERT, {
          message: 'campaign.merge-campaign.error',
          type: AlertType.ERROR,
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.closeDialog();
      });
  }

  isAllInputValid() {
    LOG.debug('checking if form is valid');
    const ref = this.$refs.form as VForm;
    if (ref === undefined) {
      LOG.warn('form ref is undefined');
      return false;
    }
    return ref.validate();
  }

  clearInputs() {
    this.selectedDestinationId = '';
    this.possibleDestinations = [];
  }
}
